import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { BookOpen, Upload, Trash2, Link2 } from 'lucide-react';
import { askConfirm } from '../lib/askConfirm';

const API = (agent) => `${process.env.REACT_APP_BACKEND_URL}/api/voice/${agent}/kb`;
const own = () => ({ 'X-Owner-Token': localStorage.getItem('giss_owner_token') || '' });

// owner: documents of the DripX knowledge base (stored at ElevenLabs, used by the agent in its answers)
export function DripXLibrary({ t, agent = 'dripx', name = 'DripX' }) {
  const [docs, setDocs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState('');
  const inp = useRef(null);
  const call = async (path, opts = {}) => {
    const r = await fetch(`${API(agent)}${path}`, { ...opts, headers: { ...own(), ...(opts.headers || {}) } });
    if (r.status === 401) throw new Error(t.ownerExpired);
    if (!r.ok) throw new Error(`${t.dxLibErr}: ${(await r.text()).slice(0, 160)}`);
    return r.json();
  };
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(String(e.message || e)); } setBusy(false); };
  const load = () => run(async () => setDocs((await call('')).docs));
  const upload = (file) => file && run(async () => { const fd = new FormData(); fd.append('file', file); const d = await call('/file', { method: 'POST', body: fd }); setDocs((l) => [...(l || []), d]); toast.success(t.dxLibDone); });
  const addUrl = () => url && run(async () => { const d = await call('/url', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url }) }); setDocs((l) => [...(l || []), d]); setUrl(''); toast.success(t.dxLibDone); });
  const del = async (id) => (await askConfirm(t.dxLibDelAsk, { ok: t.dxLibDel, cancel: t.btnCancel })) && run(async () => { await call(`/${id}`, { method: 'DELETE' }); setDocs((l) => l.filter((x) => x.id !== id)); });
  return (
    <section className="fn-box" data-testid={`${agent}-library`}>
      <button className="btn btn-outline btn-sm" data-testid={`${agent}-lib-load-btn`} onClick={load} disabled={busy}><BookOpen size={15} /> {t.dxLib.replace('DripX', name)}{docs ? ` (${docs.length})` : ''}</button>
      {docs && <>
        <small className="muted">{t.dxLibHint.replace('DripX', name)}</small>
        {!docs.length && <p className="muted" data-testid={`${agent}-lib-empty`}>{t.dxLibEmpty}</p>}
        {docs.map((d) => (
          <div key={d.id} className="dx-lib-item" data-testid={`${agent}-lib-doc-${d.id}`}>
            <span>{d.type === 'url' ? '🌐' : '📄'} {d.name}</span>
            <button className="icon-btn danger" data-testid={`${agent}-lib-del-${d.id}`} title={t.dxLibDel} aria-label={t.dxLibDel} disabled={busy} onClick={() => del(d.id)}><Trash2 size={14} /></button>
          </div>
        ))}
        <input ref={inp} type="file" hidden accept=".pdf,.docx,.txt,.html,.htm,.epub,.md" data-testid={`${agent}-lib-file-input`} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
        <div className="row">
          <button className="btn btn-proj btn-sm" data-testid={`${agent}-lib-upload-btn`} disabled={busy} onClick={() => inp.current?.click()}><Upload size={14} /> {busy ? t.dxLibBusy : t.dxLibUpload}</button>
        </div>
        <div className="row">
          <input data-testid={`${agent}-lib-url-input`} value={url} placeholder={t.dxLibUrl} onChange={(e) => setUrl(e.target.value.trim())} />
          <button className="btn btn-outline btn-sm" data-testid={`${agent}-lib-url-btn`} disabled={busy || !/^https?:\/\//.test(url)} onClick={addUrl}><Link2 size={14} /> {t.dxLibUrlAdd}</button>
        </div>
      </>}
    </section>
  );
}
